import React, { useEffect, useState } from 'react';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import AgentCustomerCreateModal from './AgentCustomerCreateModal';
import { Search, Building2, Plus, Pencil, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  lead: 'bg-amber-100 text-amber-800',
  prospect: 'bg-blue-100 text-blue-800',
  customer: 'bg-green-100 text-green-800',
  vip: 'bg-purple-100 text-purple-800',
};

const AgentCustomers = () => {
  const { agentToken } = useAgentAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // customer object being edited

  const API = process.env.REACT_APP_BACKEND_URL;

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Eliminare il cliente ${c.companyName || c.firstName + ' ' + c.lastName}?\n(Se ha ordini/preventivi verrà solo disassegnato.)`)) return;
    const res = await fetch(`${API}/api/agent/customers/${c.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${agentToken}` },
    });
    const j = await res.json();
    alert(j.message || 'Operazione completata');
    load();
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      const res = await fetch(`${API}/api/agent/customers?${params}`, {
        headers: { Authorization: `Bearer ${agentToken}` },
      });
      const j = await res.json();
      setCustomers(j.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (agentToken) load(); /* eslint-disable-next-line */ }, [agentToken]);
  useEffect(() => { if (agentToken) { const t = setTimeout(load, 300); return () => clearTimeout(t); } /* eslint-disable-next-line */ }, [search, status]);

  return (
    <AgentLayout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">I miei clienti</h1>
          <p className="text-sm text-gray-600 mt-1">{customers.length} contatti nel tuo portafoglio.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…"
              data-testid="agent-customers-search"
              className="pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm w-56 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="agent-customers-status"
            className="px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option value="">Tutti gli stati</option>
            <option value="lead">Lead</option>
            <option value="prospect">Prospect</option>
            <option value="customer">Cliente</option>
            <option value="vip">VIP</option>
          </select>
          <button onClick={() => setShowCreate(true)} data-testid="btn-new-customer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium">
            <Plus className="w-4 h-4" /> Nuovo cliente
          </button>
        </div>
      </div>

      <AgentCustomerCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => load()}
        agentToken={agentToken}
      />

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Caricamento…</div>
        ) : customers.length === 0 ? (
          <div className="py-16 text-center text-gray-500">Nessun cliente assegnato al momento.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-purple-50/50 text-purple-700 uppercase text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-left px-4 py-3">Azienda</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Contatto</th>
                <th className="text-left px-4 py-3">Assegnato il</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map((c) => (
                <tr key={c.id} className="hover:bg-purple-50/40" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-xs font-bold">
                        {(c.firstName?.[0] || '?') + (c.lastName?.[0] || '')}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{c.firstName} {c.lastName}</div>
                        <div className="text-[11px] text-gray-500">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {c.companyName ? (
                      <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-purple-500" />{c.companyName}</span>
                    ) : (
                      <span className="text-gray-400 text-xs italic">Privato</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[c.customerStatus] || 'bg-gray-100 text-gray-700'}`}>
                      {c.customerStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{c.phone || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {c.assignedAt ? new Date(c.assignedAt).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(c)} title="Modifica"
                        data-testid={`btn-edit-customer-${c.id}`}
                        className="p-1.5 rounded hover:bg-purple-100">
                        <Pencil className="w-4 h-4 text-purple-600" />
                      </button>
                      <button onClick={() => deleteCustomer(c)} title="Elimina"
                        data-testid={`btn-delete-customer-${c.id}`}
                        className="p-1.5 rounded hover:bg-red-50">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <AgentCustomerCreateModal
          open={!!editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
          agentToken={agentToken}
          initialCustomer={editing}
        />
      )}
    </AgentLayout>
  );
};

export default AgentCustomers;
